import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  BookUser,
  Cloud,
  Contact,
  Info,
  KeyRound,
  List,
  Mail,
  MapPin,
  Phone,
  Play,
  RefreshCw,
  Save,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  History,
  Loader2,
} from 'lucide-react';
import SyncScheduling from './SyncScheduling';
import { toast } from 'sonner';
import { API_URL } from '../apiBase';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

const DEFAULT_CONTACT_SYNC_FIELDS = {
  email_principal: true,
  email_secondaire: true,
  email_personnel: false,
  telephone_mobile: true,
  telephone_fixe: true,
  telephone_personnel: false,
  societe: true,
  poste: true,
  adresse: true,
};

const CONTACT_FIELD_GROUPS = [
  {
    title: 'E-mails',
    icon: Mail,
    fields: [
      { key: 'email_principal', label: 'Adresse e-mail principale (professionnelle)' },
      { key: 'email_secondaire', label: 'Adresse e-mail secondaire (professionnelle)' },
      { key: 'email_personnel', label: 'Adresse e-mail personnelle' },
    ],
  },
  {
    title: 'Téléphones',
    icon: Phone,
    fields: [
      { key: 'telephone_mobile', label: 'Téléphone mobile professionnel' },
      { key: 'telephone_fixe', label: 'Téléphone fixe professionnel' },
      { key: 'telephone_personnel', label: 'Téléphone personnel' },
    ],
  },
  {
    title: 'Autres informations',
    icon: MapPin,
    fields: [
      { key: 'societe', label: 'Société' },
      { key: 'poste', label: 'Poste / fonction' },
      { key: 'adresse', label: 'Adresse postale' },
    ],
  },
];

const emptyForm = {
  exchange_email: '',
  exchange_display_name: '',
  azure_tenant_id: '',
  azure_client_id: '',
  azure_client_secret: '',
  sync_contacts: true,
  sync_lists: true,
  contact_sync_fields: { ...DEFAULT_CONTACT_SYNC_FIELDS },
  list_sync_selection: {},
  predefined_lists: [],
};

function formatDateTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

const SYNC_SCOPE_LABELS = {
  contacts: 'Contacts',
  lists: 'Listes',
  all: 'Contacts et listes',
};

function historyStatusBadge(status) {
  if (status === 'success') {
    return <Badge className="bg-emerald-600 hover:bg-emerald-600">Succès</Badge>;
  }
  if (status === 'partial') {
    return (
      <Badge variant="secondary" className="bg-amber-500/90 text-white hover:bg-amber-500/90">
        Partiel
      </Badge>
    );
  }
  return <Badge variant="destructive">Erreur</Badge>;
}

function contactsSummaryLine(summary) {
  if (!summary) return '—';
  const parts = [];
  if (summary.contacts_created) parts.push(`${summary.contacts_created} créé${summary.contacts_created > 1 ? 's' : ''}`);
  if (summary.contacts_updated) parts.push(`${summary.contacts_updated} maj`);
  if (summary.contacts_removed) parts.push(`${summary.contacts_removed} suppr.`);
  if (summary.contacts_duplicates_removed) {
    parts.push(`${summary.contacts_duplicates_removed} doublon${summary.contacts_duplicates_removed > 1 ? 's' : ''}`);
  }
  if (summary.contacts_orphans_removed) {
    parts.push(`${summary.contacts_orphans_removed} coquille${summary.contacts_orphans_removed > 1 ? 's' : ''}`);
  }
  if (summary.contacts_photos_updated) {
    parts.push(`${summary.contacts_photos_updated} photo${summary.contacts_photos_updated > 1 ? 's' : ''}`);
  }
  return parts.length ? parts.join(' · ') : '—';
}

function listsSummaryLine(summary) {
  if (!summary) return '—';
  const parts = [];
  if (summary.lists_created) parts.push(`${summary.lists_created} créée${summary.lists_created > 1 ? 's' : ''}`);
  if (summary.lists_updated) parts.push(`${summary.lists_updated} maj`);
  if (summary.list_member_additions) {
    parts.push(`${summary.list_member_additions} ajout${summary.list_member_additions > 1 ? 's' : ''} membre`);
  }
  if (summary.lists_removed) {
    parts.push(`${summary.lists_removed} retirée${summary.lists_removed > 1 ? 's' : ''}`);
  }
  return parts.length ? parts.join(' · ') : '—';
}

function applySettingsToForm(data, prevSecret = '') {
  const fields = {
    ...DEFAULT_CONTACT_SYNC_FIELDS,
    ...(data.contact_sync_fields || {}),
  };
  const predefinedLists = data.predefined_lists || [];
  const listSyncSelection = {};
  predefinedLists.forEach((list) => {
    listSyncSelection[list.id] = list.sync_enabled !== false;
  });
  return {
    exchange_email: data.exchange_email || '',
    exchange_display_name: data.exchange_display_name || '',
    azure_tenant_id: data.azure_tenant_id || '',
    azure_client_id: data.azure_client_id || '',
    azure_client_secret: prevSecret,
    sync_contacts: data.sync_contacts !== false,
    sync_lists: data.sync_lists !== false,
    contact_sync_fields: fields,
    predefined_lists: predefinedLists,
    list_sync_selection: listSyncSelection,
  };
}

const SYNC_SUB_TITLES = {
  config: 'Configuration Compte',
  options: 'Options de synchronisation',
  status: 'État des synchronisations',
  schedule: 'Programmation',
};

const HISTORY_PAGE_SIZE = 10;

const SYNC_SCOPE_LABELS_PROGRESS = {
  contacts: 'contacts',
  lists: 'listes',
  all: 'complète',
};

function initialSyncProgressTotal(scope, stats, form) {
  const contacts = stats?.contacts_count || 0;
  const listsFromForm = Object.values(form?.list_sync_selection || {}).filter(Boolean).length;
  const lists =
    listsFromForm || stats?.lists_sync_selected_count || stats?.lists_global_count || 0;
  if (scope === 'contacts') return contacts;
  if (scope === 'lists') return lists;
  return contacts;
}

function SyncProgressIndicator({ progress }) {
  if (!progress) return null;

  const current = progress.current ?? 0;
  const total = progress.total ?? 0;
  const progressPct = total > 0 ? Math.min(100, (current / total) * 100) : 0;
  const scopeLabel = SYNC_SCOPE_LABELS_PROGRESS[progress.scope] || 'en cours';
  const phaseLabel =
    progress.scope === 'all' && progress.phase === 'contacts'
      ? 'contacts'
      : progress.scope === 'all' && progress.phase === 'lists'
        ? 'listes'
        : null;

  return (
    <div
      className="mt-3 w-full rounded-lg border border-primary/25 bg-primary/5 p-3 space-y-2"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden />
        <span>Synchronisation {scopeLabel} en cours…</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        État actuel{phaseLabel ? ` (${phaseLabel})` : ''} :{' '}
        <strong className="text-foreground">
          {current}/{total}
        </strong>
        {progress.label ? (
          <>
            {' · '}
            <span className="text-foreground">{progress.label}</span>
          </>
        ) : null}
      </p>
    </div>
  );
}

const SyncAdministration = ({ syncSubTab = 'config' }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingContacts, setSavingContacts] = useState(false);
  const [savingLists, setSavingLists] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [runningContacts, setRunningContacts] = useState(false);
  const [runningLists, setRunningLists] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [secretConfigured, setSecretConfigured] = useState(false);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [lastSync, setLastSync] = useState({
    at: null,
    status: null,
    summary: null,
  });
  const [syncHistory, setSyncHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [stats, setStats] = useState({
    contacts_count: 0,
    lists_total_count: 0,
    lists_global_count: 0,
    lists_personal_count: 0,
    lists_sync_selected_count: 0,
  });

  const savedSyncOptionsRef = useRef(null);
  const prevSyncSubTabRef = useRef(syncSubTab);
  const syncRunning = running || runningContacts || runningLists;

  useEffect(() => {
    if (!syncRunning) return undefined;

    let cancelled = false;

    const pollProgress = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/sync/progress`);
        if (cancelled || !data?.running) return;
        setSyncProgress({
          scope: data.scope,
          phase: data.phase,
          current: data.current ?? 0,
          total: data.total ?? 0,
          label: data.label ?? null,
        });
      } catch {
        // Ignorer les erreurs de polling pendant une synchronisation longue.
      }
    };

    pollProgress();
    const intervalId = window.setInterval(pollProgress, 500);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [syncRunning]);

  const snapshotSyncOptions = useCallback((data) => {
    const applied = applySettingsToForm(data, '');
    savedSyncOptionsRef.current = {
      sync_contacts: applied.sync_contacts,
      sync_lists: applied.sync_lists,
      contact_sync_fields: { ...applied.contact_sync_fields },
      list_sync_selection: { ...applied.list_sync_selection },
      predefined_lists: applied.predefined_lists.map((list) => ({ ...list })),
    };
  }, []);

  const snapshotContactSyncOptions = useCallback((data) => {
    const applied = applySettingsToForm(data, '');
    const current = savedSyncOptionsRef.current || {
      sync_lists: applied.sync_lists,
      list_sync_selection: { ...applied.list_sync_selection },
      predefined_lists: applied.predefined_lists.map((list) => ({ ...list })),
      contact_sync_fields: { ...applied.contact_sync_fields },
      sync_contacts: applied.sync_contacts,
    };
    savedSyncOptionsRef.current = {
      ...current,
      sync_contacts: applied.sync_contacts,
      contact_sync_fields: { ...applied.contact_sync_fields },
    };
  }, []);

  const snapshotListSyncOptions = useCallback((data) => {
    const applied = applySettingsToForm(data, '');
    const current = savedSyncOptionsRef.current || {
      sync_contacts: applied.sync_contacts,
      contact_sync_fields: { ...applied.contact_sync_fields },
      sync_lists: applied.sync_lists,
      list_sync_selection: { ...applied.list_sync_selection },
      predefined_lists: applied.predefined_lists.map((list) => ({ ...list })),
    };
    savedSyncOptionsRef.current = {
      ...current,
      sync_lists: applied.sync_lists,
      list_sync_selection: { ...applied.list_sync_selection },
      predefined_lists: applied.predefined_lists.map((list) => ({ ...list })),
    };
  }, []);

  const applyContactSettingsResponse = useCallback((data, keepSecret) => {
    setForm((prev) => {
      const applied = applySettingsToForm(data, keepSecret ? prev.azure_client_secret : '');
      return {
        ...prev,
        sync_contacts: applied.sync_contacts,
        contact_sync_fields: applied.contact_sync_fields,
      };
    });
    setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
    snapshotContactSyncOptions(data);
  }, [snapshotContactSyncOptions]);

  const applyListSettingsResponse = useCallback((data, keepSecret) => {
    setForm((prev) => {
      const applied = applySettingsToForm(data, keepSecret ? prev.azure_client_secret : '');
      return {
        ...prev,
        sync_lists: applied.sync_lists,
        list_sync_selection: applied.list_sync_selection,
        predefined_lists: applied.predefined_lists,
      };
    });
    setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
    if (data.annuaire) setStats(data.annuaire);
    snapshotListSyncOptions(data);
  }, [snapshotListSyncOptions]);

  const restoreSavedSyncOptions = useCallback(() => {
    const snap = savedSyncOptionsRef.current;
    if (!snap) return;
    setForm((f) => ({
      ...f,
      sync_contacts: snap.sync_contacts,
      sync_lists: snap.sync_lists,
      contact_sync_fields: { ...snap.contact_sync_fields },
      list_sync_selection: { ...snap.list_sync_selection },
      predefined_lists: snap.predefined_lists.map((list) => ({ ...list })),
    }));
  }, []);

  const applyResponse = useCallback((data, keepSecret) => {
    setForm((prev) => applySettingsToForm(data, keepSecret ? prev.azure_client_secret : ''));
    setSecretConfigured(Boolean(data.azure_client_secret_configured));
    setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
    setLastSync({
      at: data.last_sync_at,
      status: data.last_sync_status,
      summary: data.last_sync_summary,
    });
    if (data.annuaire) setStats(data.annuaire);
    snapshotSyncOptions(data);
  }, [snapshotSyncOptions]);

  const loadSettings = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/sync/settings`);
      applyResponse(data, true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : 'Impossible de charger la configuration Exchange',
      );
    } finally {
      if (!silent) setLoading(false);
    }
  }, [applyResponse]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/sync/history`);
      setSyncHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : 'Impossible de charger l’historique des synchronisations',
      );
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    const previous = prevSyncSubTabRef.current;
    if (previous === 'options' && syncSubTab !== 'options') {
      restoreSavedSyncOptions();
    }
    if (syncSubTab === 'options' && previous !== 'options') {
      loadSettings({ silent: true });
    }
    if (syncSubTab === 'status') {
      loadHistory();
    }
    prevSyncSubTabRef.current = syncSubTab;
  }, [syncSubTab, loadSettings, restoreSavedSyncOptions, loadHistory]);

  const historyPageCount = Math.max(1, Math.ceil(syncHistory.length / HISTORY_PAGE_SIZE));
  const paginatedHistory = syncHistory.slice(
    (historyPage - 1) * HISTORY_PAGE_SIZE,
    historyPage * HISTORY_PAGE_SIZE,
  );

  useEffect(() => {
    setHistoryPage(1);
    setExpandedHistoryId(null);
  }, [syncHistory.length]);

  useEffect(() => {
    if (historyPage > historyPageCount) {
      setHistoryPage(historyPageCount);
    }
  }, [historyPage, historyPageCount]);

  useEffect(() => {
    setExpandedHistoryId(null);
  }, [historyPage]);

  const buildPayload = () => ({
    exchange_email: form.exchange_email.trim(),
    exchange_display_name: form.exchange_display_name.trim() || null,
    azure_tenant_id: form.azure_tenant_id.trim(),
    azure_client_id: form.azure_client_id.trim(),
    sync_contacts: form.sync_contacts,
    sync_lists: form.sync_lists,
    contact_sync_fields: form.contact_sync_fields,
    list_sync_selection: form.list_sync_selection,
    ...(form.azure_client_secret.trim()
      ? { azure_client_secret: form.azure_client_secret.trim() }
      : {}),
  });

  const setContactField = (key, checked) => {
    setForm((f) => ({
      ...f,
      contact_sync_fields: { ...f.contact_sync_fields, [key]: Boolean(checked) },
    }));
  };

  const setListSyncEnabled = (listId, checked) => {
    setForm((f) => ({
      ...f,
      list_sync_selection: { ...f.list_sync_selection, [listId]: Boolean(checked) },
      predefined_lists: f.predefined_lists.map((list) =>
        list.id === listId ? { ...list, sync_enabled: Boolean(checked) } : list,
      ),
    }));
  };

  const validateConfig = () => {
    if (!form.exchange_email.trim()) {
      toast.error('Veuillez saisir une adresse Exchange (Configuration Compte)');
      return false;
    }
    if (!form.azure_tenant_id.trim() || !form.azure_client_id.trim()) {
      toast.error('Tenant ID et Client ID sont requis (Configuration Compte)');
      return false;
    }
    if (!secretConfigured && !form.azure_client_secret.trim()) {
      toast.error('Le secret client est requis lors de la première configuration');
      return false;
    }
    return true;
  };

  const persistSettings = async () => {
    const { data } = await axios.put(`${API_URL}/api/sync/settings`, buildPayload());
    applyResponse(data, false);
    return data;
  };

  const persistContactSettings = async () => {
    const { data } = await axios.patch(`${API_URL}/api/sync/settings/contacts`, {
      sync_contacts: form.sync_contacts,
      contact_sync_fields: form.contact_sync_fields,
    });
    applyContactSettingsResponse(data, false);
    return data;
  };

  const persistListSettings = async () => {
    const { data } = await axios.patch(`${API_URL}/api/sync/settings/lists`, {
      sync_lists: form.sync_lists,
      list_sync_selection: form.list_sync_selection,
    });
    applyListSettingsResponse(data, false);
    return data;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateConfig()) return;

    setSaving(true);
    try {
      await persistSettings();
      toast.success('Configuration enregistrée');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error('Vérifiez les champs saisis (e-mail, Tenant ID, Client ID)');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Erreur lors de l’enregistrement');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContacts = async () => {
    if (!validateConfig()) return;
    setSavingContacts(true);
    try {
      await persistContactSettings();
      toast.success('Options contacts enregistrées');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de l’enregistrement');
    } finally {
      setSavingContacts(false);
    }
  };

  const handleSaveLists = async () => {
    if (!validateConfig()) return;
    setSavingLists(true);
    try {
      await persistListSettings();
      toast.success('Options listes enregistrées');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de l’enregistrement');
    } finally {
      setSavingLists(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/sync/test-connection`);
      if (data.ok) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Échec du test de connexion');
    } finally {
      setTesting(false);
    }
  };

  const handleRunSync = async (scope = null) => {
    const isContactsOnly = scope === 'contacts';
    const isListsOnly = scope === 'lists';
    const setRunner = isContactsOnly
      ? setRunningContacts
      : isListsOnly
        ? setRunningLists
        : setRunning;
    const payload =
      isContactsOnly
        ? { sync_contacts: true, sync_lists: false }
        : isListsOnly
          ? { sync_contacts: false, sync_lists: true }
          : {};

    if (isContactsOnly && !form.sync_contacts) {
      toast.error('Activez la synchronisation des contacts avant de lancer');
      return;
    }
    if (isListsOnly && !form.sync_lists) {
      toast.error('Activez la synchronisation des listes avant de lancer');
      return;
    }
    if (!validateConfig()) return;

    const scopeKey = isContactsOnly ? 'contacts' : isListsOnly ? 'lists' : 'all';
    setSyncProgress({
      scope: scopeKey,
      phase: isListsOnly ? 'lists' : 'contacts',
      current: 0,
      total: initialSyncProgressTotal(scopeKey, stats, form),
    });
    setRunner(true);
    try {
      if (isContactsOnly) {
        await persistContactSettings();
      } else if (isListsOnly) {
        await persistListSettings();
      } else {
        await persistSettings();
      }
      const { data } = await axios.post(`${API_URL}/api/sync/run`, payload);
      setLastSync({
        at: data.finished_at,
        status: data.summary?.errors?.length ? 'partial' : 'success',
        summary: data.summary,
      });
      toast.success(data.message);
      await loadSettings();
      await loadHistory();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Échec de la synchronisation');
      await loadSettings();
    } finally {
      setSyncProgress(null);
      setRunner(false);
    }
  };

  const renderHistoryDetails = (summary) => {
    if (!summary) return null;
    return (
      <div className="space-y-3 text-sm">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <p>Contacts créés : <strong>{summary.contacts_created ?? 0}</strong></p>
          <p>Contacts mis à jour : <strong>{summary.contacts_updated ?? 0}</strong></p>
          <p>Contacts supprimés : <strong>{summary.contacts_removed ?? 0}</strong></p>
          <p>Photos mises à jour : <strong>{summary.contacts_photos_updated ?? 0}</strong></p>
          <p>Doublons supprimés : <strong>{summary.contacts_duplicates_removed ?? 0}</strong></p>
          <p>Coquilles supprimées : <strong>{summary.contacts_orphans_removed ?? 0}</strong></p>
          <p>Listes créées : <strong>{summary.lists_created ?? 0}</strong></p>
          <p>Listes mises à jour : <strong>{summary.lists_updated ?? 0}</strong></p>
          <p>Ajouts membres (listes) : <strong>{summary.list_member_additions ?? 0}</strong></p>
          <p>Listes retirées : <strong>{summary.lists_removed ?? 0}</strong></p>
        </div>
        {summary.errors?.length > 0 && (
          <ul className="max-h-40 overflow-y-auto rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            {summary.errors.map((err) => (
              <li key={err} className="mb-1 last:mb-0">
                {err}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="mt-4 text-sm text-muted-foreground">Chargement de la configuration…</p>
        </div>
      </div>
    );
  }

  const sectionTitle = SYNC_SUB_TITLES[syncSubTab] || SYNC_SUB_TITLES.config;
  const showSave = syncSubTab === 'config';
  const showTest = syncSubTab === 'config';
  const showRunSync = syncSubTab === 'status';
  const optionsBusy =
    savingContacts || savingLists || runningContacts || runningLists;

  const renderCardActions = ({
    onSave,
    onRunSync,
    savingCard,
    runningCard,
    runDisabled,
    syncScope,
  }) => (
    <div className="mt-4 border-t border-border pt-4 space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={savingCard || runningCard || optionsBusy}
          onClick={onSave}
        >
          <Save className="mr-2 h-4 w-4" aria-hidden />
          {savingCard ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
        <Button
          type="button"
          disabled={savingCard || runningCard || runDisabled || optionsBusy}
          onClick={onRunSync}
        >
          {runningCard ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Play className="mr-2 h-4 w-4" aria-hidden />
          )}
          {runningCard ? 'Synchronisation…' : 'Lancer la synchronisation'}
        </Button>
      </div>
      {runningCard && syncProgress?.scope === syncScope && (
        <SyncProgressIndicator progress={syncProgress} />
      )}
    </div>
  );

  const mailboxCard = (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Mail className="h-5 w-5 text-primary" aria-hidden />
          Compte Mail
        </CardTitle>
        <CardDescription>
          Boîte partagée ou compte technique où seront créés les contacts miroir et associées les
          listes.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[38%]">Paramètre</TableHead>
                <TableHead>Valeur</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="align-top font-medium">Adresse e-mail Exchange *</TableCell>
                <TableCell>
                  <Input
                    id="exchange-email"
                    type="email"
                    autoComplete="off"
                    placeholder="annuaire-sync@votredomaine.fr"
                    value={form.exchange_email}
                    onChange={(e) => setForm((f) => ({ ...f, exchange_email: e.target.value }))}
                    className="w-full"
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="align-top font-medium">Libellé (optionnel)</TableCell>
                <TableCell>
                  <Input
                    id="exchange-display-name"
                    type="text"
                    autoComplete="off"
                    placeholder="Annuaire interne — miroir"
                    value={form.exchange_display_name}
                    onChange={(e) => setForm((f) => ({ ...f, exchange_display_name: e.target.value }))}
                    className="w-full"
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );

  const entraCard = (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Cloud className="h-5 w-5 text-primary" aria-hidden />
          Lien Entra
        </CardTitle>
        <CardDescription>
          Identifiants de l&apos;application enregistrée dans le portail Azure (flux OAuth
          « credentials client » — pas le mot de passe utilisateur).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[38%]">Paramètre</TableHead>
                <TableHead>Valeur</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="align-top font-medium">Tenant ID (annuaire) *</TableCell>
                <TableCell>
                  <Input
                    id="azure-tenant-id"
                    type="text"
                    autoComplete="off"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={form.azure_tenant_id}
                    onChange={(e) => setForm((f) => ({ ...f, azure_tenant_id: e.target.value }))}
                    className="w-full font-mono text-sm"
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="align-top font-medium">Client ID (application) *</TableCell>
                <TableCell>
                  <Input
                    id="azure-client-id"
                    type="text"
                    autoComplete="off"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={form.azure_client_id}
                    onChange={(e) => setForm((f) => ({ ...f, azure_client_id: e.target.value }))}
                    className="w-full font-mono text-sm"
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="align-top font-medium">
                  <span className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4" aria-hidden />
                    Secret client
                    {secretConfigured && (
                      <Badge variant="outline" className="text-[10px] font-normal">
                        Configuré
                      </Badge>
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  <Input
                    id="azure-client-secret"
                    type="password"
                    autoComplete="new-password"
                    placeholder={
                      secretConfigured
                        ? 'Laisser vide pour conserver le secret actuel'
                        : 'Secret de l’application Azure'
                    }
                    value={form.azure_client_secret}
                    onChange={(e) => setForm((f) => ({ ...f, azure_client_secret: e.target.value }))}
                    className="w-full"
                  />
                  <p className="mt-2 text-xs text-muted-foreground">
                    Permissions Graph requises (consentement admin) : accès aux contacts de la boîte
                    cible, création/gestion de groupes de distribution, lecture des utilisateurs du
                    tenant.
                  </p>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );

  const accountConfigSection = (
    <div className="w-full">
      <div className="mx-auto grid w-full grid-cols-1 items-stretch gap-6 lg:grid-cols-2 lg:gap-8">
        <div className="min-w-0 px-4 lg:px-6">{mailboxCard}</div>
        <div className="min-w-0 px-4 lg:px-6">{entraCard}</div>
      </div>
    </div>
  );

  const contactsOptionsCard = (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Contact className="h-5 w-5 text-primary" aria-hidden />
          Options de synchronisation des Contacts
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex cursor-pointer items-center gap-3">
          <Checkbox
            checked={form.sync_contacts}
            onCheckedChange={(v) => setForm((f) => ({ ...f, sync_contacts: Boolean(v) }))}
          />
          <span className="text-sm">Synchroniser les contacts de l&apos;annuaire</span>
        </label>

        {form.sync_contacts && (
          <div className="mt-4 overflow-hidden rounded-lg border border-border bg-muted/20">
            <div className="border-b border-border px-4 py-3">
              <p className="text-sm font-medium text-foreground">Champs des contacts à synchroniser</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Cochez les informations à inclure dans chaque contact Exchange. Les champs
                décochés sont retirés lors de la prochaine synchronisation. Le nom reste
                toujours synchronisé.
              </p>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12" />
                  <TableHead>Champ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {CONTACT_FIELD_GROUPS.map((group) => {
                  const GroupIcon = group.icon;
                  return (
                    <React.Fragment key={group.title}>
                      <TableRow className="bg-muted/40 hover:bg-muted/40">
                        <TableCell colSpan={2} className="py-2">
                          <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            <GroupIcon className="h-3.5 w-3.5" aria-hidden />
                            {group.title}
                          </span>
                        </TableCell>
                      </TableRow>
                      {group.fields.map((field) => (
                        <TableRow key={field.key}>
                          <TableCell className="align-top">
                            <Checkbox
                              checked={Boolean(form.contact_sync_fields[field.key])}
                              onCheckedChange={(v) => setContactField(field.key, v)}
                            />
                          </TableCell>
                          <TableCell className="text-sm leading-snug">{field.label}</TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {renderCardActions({
          onSave: handleSaveContacts,
          onRunSync: () => handleRunSync('contacts'),
          savingCard: savingContacts,
          runningCard: runningContacts,
          runDisabled: !form.sync_contacts,
          syncScope: 'contacts',
        })}
      </CardContent>
    </Card>
  );

  const listsOptionsCard = (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <List className="h-5 w-5 text-primary" aria-hidden />
          Options de synchronisation des Listes
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex cursor-pointer items-center gap-3">
          <Checkbox
            checked={form.sync_lists}
            onCheckedChange={(v) => setForm((f) => ({ ...f, sync_lists: Boolean(v) }))}
          />
          <span className="text-sm">
            Activer la synchronisation des listes vers Exchange (groupes Microsoft 365)
          </span>
        </label>

        {form.sync_lists && (
          <div className="mt-4 overflow-hidden rounded-lg border border-border bg-muted/20">
            <div className="border-b border-border px-4 py-3">
              <p className="text-sm font-medium text-foreground">Listes prédéfinies à synchroniser</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Cochez les listes à publier sous forme de groupes Microsoft 365. Une liste
                décochée sera retirée d&apos;Exchange lors de la prochaine synchronisation.
              </p>
            </div>
            {form.predefined_lists.length === 0 ? (
              <p className="px-4 py-6 text-sm text-muted-foreground">Aucune liste prédéfinie disponible.</p>
            ) : (
              <div className="max-h-[28rem] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12" />
                      <TableHead>Liste</TableHead>
                      <TableHead className="w-28 text-right">Membres</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.predefined_lists.map((list) => (
                      <TableRow key={list.id}>
                        <TableCell>
                          <Checkbox
                            checked={Boolean(form.list_sync_selection[list.id])}
                            onCheckedChange={(v) => setListSyncEnabled(list.id, v)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{list.name}</TableCell>
                        <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                          {typeof list.member_count === 'number'
                            ? `${list.member_count} membre${list.member_count > 1 ? 's' : ''}`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
        {renderCardActions({
          onSave: handleSaveLists,
          onRunSync: () => handleRunSync('lists'),
          savingCard: savingLists,
          runningCard: runningLists,
          runDisabled: !form.sync_lists,
          syncScope: 'lists',
        })}
      </CardContent>
    </Card>
  );

  const optionsSection = (
    <div className="w-full">
      <div className="mx-auto grid w-full grid-cols-1 items-stretch gap-6 lg:grid-cols-2 lg:gap-8">
        <div className="min-w-0 px-4 lg:px-6">{contactsOptionsCard}</div>
        <div className="min-w-0 px-4 lg:px-6">{listsOptionsCard}</div>
      </div>
    </div>
  );

  const perimeterCard = (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Info className="h-5 w-5 text-primary" aria-hidden />
          Périmètre annuaire
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-3">
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Contact className="h-4 w-4 text-primary" aria-hidden />
              Contacts
            </div>
            <p className="mt-2 text-2xl font-semibold tabular-nums">{stats.contacts_count}</p>
          </div>
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <List className="h-4 w-4 text-primary" aria-hidden />
              Listes
            </div>
            <p className="mt-2 text-2xl font-semibold tabular-nums">
              {stats.lists_sync_selected_count ?? stats.lists_global_count}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {stats.lists_sync_selected_count ?? stats.lists_global_count} sur{' '}
              {stats.lists_global_count} prédéfinie
              {stats.lists_global_count > 1 ? 's' : ''} sélectionnée
              {(stats.lists_sync_selected_count ?? stats.lists_global_count) > 1 ? 's' : ''}
              {stats.lists_personal_count > 0 && (
                <>
                  {' '}
                  · {stats.lists_personal_count} personnelle
                  {stats.lists_personal_count > 1 ? 's' : ''} exclue
                  {stats.lists_personal_count > 1 ? 's' : ''}
                </>
              )}
            </p>
          </div>
        </div>
        <p className="flex gap-2 text-sm text-muted-foreground">
          <BookUser className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
          <span>
            Sens unique : <strong className="font-medium text-foreground">Annuaire Interne → Exchange</strong>.
            Seules les <strong className="font-medium text-foreground">listes prédéfinies</strong>{' '}
            sont synchronisées. Les membres doivent exister comme utilisateurs dans le tenant
            Microsoft.
          </span>
        </p>
      </CardContent>
    </Card>
  );

  const statusSection = (
    <div className="grid w-full gap-4 lg:grid-cols-[minmax(220px,260px)_minmax(0,1fr)] lg:items-start">
      {perimeterCard}
      <Card className="min-w-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="h-5 w-5 text-primary" aria-hidden />
            Historique des synchronisations
          </CardTitle>
          <CardDescription>
            Les 100 dernières exécutions (contacts, listes ou les deux).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              Chargement de l&apos;historique…
            </div>
          ) : syncHistory.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Aucune synchronisation n&apos;a encore été lancée.
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Date</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Périmètre</TableHead>
                    <TableHead>Contacts</TableHead>
                    <TableHead>Listes</TableHead>
                    <TableHead>Par</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedHistory.map((entry) => {
                    const expanded = expandedHistoryId === entry.id;
                    return (
                      <React.Fragment key={entry.id}>
                        <TableRow
                          className="cursor-pointer"
                          onClick={() =>
                            setExpandedHistoryId(expanded ? null : entry.id)
                          }
                        >
                          <TableCell className="text-muted-foreground">
                            {expanded ? (
                              <ChevronDown className="h-4 w-4" aria-hidden />
                            ) : (
                              <ChevronRight className="h-4 w-4" aria-hidden />
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap tabular-nums">
                            {formatDateTime(entry.finished_at)}
                          </TableCell>
                          <TableCell>{historyStatusBadge(entry.status)}</TableCell>
                          <TableCell>{SYNC_SCOPE_LABELS[entry.scope] || entry.scope}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {contactsSummaryLine(entry.summary)}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {listsSummaryLine(entry.summary)}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {entry.run_by || '—'}
                          </TableCell>
                        </TableRow>
                        {expanded && (
                          <TableRow className="bg-muted/20 hover:bg-muted/20">
                            <TableCell colSpan={7} className="p-4">
                              {renderHistoryDetails(entry.summary)}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  {syncHistory.length} entrée{syncHistory.length > 1 ? 's' : ''} ·{' '}
                  {HISTORY_PAGE_SIZE} par page · page {historyPage} sur {historyPageCount}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={historyPage <= 1}
                    onClick={() => setHistoryPage((page) => Math.max(1, page - 1))}
                  >
                    Précédent
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={historyPage >= historyPageCount}
                    onClick={() =>
                      setHistoryPage((page) => Math.min(historyPageCount, page + 1))
                    }
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const pageWidthClass =
    syncSubTab === 'status' ||
    syncSubTab === 'options' ||
    syncSubTab === 'config' ||
    syncSubTab === 'schedule'
      ? 'w-full max-w-none space-y-6'
      : 'mx-auto max-w-3xl space-y-6';

  const wideLayoutTab = syncSubTab === 'options' || syncSubTab === 'config';

  return (
    <div className={pageWidthClass}>
      <div className={wideLayoutTab ? 'px-4 lg:px-6' : undefined}>
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-foreground">
          <RefreshCw className="h-6 w-6 text-primary" strokeWidth={2} aria-hidden />
          Synchronisation Exchange
        </h1>
        <p className="mt-1 text-sm font-medium text-foreground">{sectionTitle}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Miroir de l&apos;annuaire interne via Microsoft Graph (application Entra ID — sans mot de passe
          de la boîte).
        </p>
      </div>

      {syncSubTab === 'schedule' ? (
        <SyncScheduling />
      ) : (
        <form onSubmit={handleSave} className="space-y-6">
          {syncSubTab === 'config' && accountConfigSection}
          {syncSubTab === 'options' && optionsSection}
          {syncSubTab === 'status' && statusSection}

          {meta.updated_at && (
            <p className="text-xs text-muted-foreground">
              Configuration modifiée le {formatDateTime(meta.updated_at)}
              {meta.updated_by ? ` par ${meta.updated_by}` : ''}
            </p>
          )}

          {(showSave || showTest || showRunSync) && (
            <div className={`space-y-3${wideLayoutTab ? ' px-4 lg:px-6' : ''}`}>
              <div className="flex flex-wrap gap-2">
                {showSave && (
                  <Button type="submit" disabled={saving}>
                    <Save className="mr-2 h-4 w-4" aria-hidden />
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                  </Button>
                )}
                {showTest && (
                  <Button type="button" variant="outline" disabled={testing || saving} onClick={handleTest}>
                    <ShieldCheck className="mr-2 h-4 w-4" aria-hidden />
                    {testing ? 'Test…' : 'Tester la connexion'}
                  </Button>
                )}
                {showRunSync && (
                  <Button
                    type="button"
                    variant="default"
                    disabled={running || saving}
                    onClick={() => handleRunSync()}
                  >
                    {running ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    ) : (
                      <Play className="mr-2 h-4 w-4" aria-hidden />
                    )}
                    {running ? 'Synchronisation…' : 'Lancer la synchronisation complète'}
                  </Button>
                )}
              </div>
              {running && syncProgress?.scope === 'all' && (
                <SyncProgressIndicator progress={syncProgress} />
              )}
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default SyncAdministration;
